import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        clients: 'Clients',
        suppliers: 'Suppliers',
        catalog: 'Car Catalog',
        brands: 'Brands',
        models: 'Models',
        vehicles: 'Vehicles',
        shipping: 'Shipping Companies',
        orders: 'Orders',
        employees: 'Employees',
        settings: 'Settings',
        logout: 'Logout',
        portal: 'My Portal',
        profile: 'Profile',
        myOrders: 'My Orders',
        documents: 'Documents'
      },
      // Auth
      auth: {
        login: 'Login',
        register: 'Register',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        name: 'Full Name',
        phone: 'Phone Number',
        forgotPassword: 'Forgot Password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        signUp: 'Sign Up',
        signIn: 'Sign In',
        welcomeBack: 'Welcome Back',
        createAccount: 'Create Account',
        adminLogin: 'Staff Login',
        clientLogin: 'Client Portal'
      },
      // Dashboard
      dashboard: {
        title: 'Dashboard',
        subtitle: 'Overview of your car import operations',
        totalClients: 'Total Clients',
        totalCars: 'Total Cars',
        pendingCars: 'Pending Cars',
        activeOrders: 'Active Orders',
        pendingDocs: 'Pending Documents',
        inTransit: 'In Transit',
        delivered: 'Delivered',
        expectedArrivals: 'Expected Arrivals',
        recentOrders: 'Recent Orders',
        ordersByStatus: 'Orders by Status',
        carsByStatus: 'Cars by Status',
        viewAll: 'View all',
        noOrders: 'No orders yet',
        noRecentOrders: 'No recent orders',
        cars: 'cars',
        dealer: 'Dealer'
      },
      // Clients
      clients: {
        title: 'Client Management',
        addClient: 'Add Client',
        editClient: 'Edit Client',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        nationalId: 'National ID',
        passport: 'Passport Number',
        dob: 'Date of Birth',
        notes: 'Notes',
        orderHistory: 'Order History',
        documents: 'Documents'
      },
      // Suppliers
      suppliers: {
        title: 'Supplier Management',
        addSupplier: 'Add Supplier',
        editSupplier: 'Edit Supplier',
        name: 'Company Name',
        contactEmail: 'Contact Email',
        contactPhone: 'Contact Phone',
        address: 'Address',
        carBrands: 'Car Brands',
        notes: 'Notes',
        active: 'Active',
        inactive: 'Inactive'
      },
      // Vehicles
      vehicles: {
        title: 'Car Catalog',
        addVehicle: 'Add Vehicle',
        editVehicle: 'Edit Vehicle',
        brand: 'Brand',
        model: 'Model',
        year: 'Year',
        version: 'Version/Trim',
        fuelType: 'Fuel Type',
        transmission: 'Transmission',
        color: 'Color',
        price: 'Price',
        photos: 'Photos',
        specs: 'Specifications'
      },
      // Shipping
      shipping: {
        title: 'Shipping Companies',
        addCompany: 'Add Company',
        editCompany: 'Edit Company',
        name: 'Company Name',
        route: 'Shipping Route',
        estimatedDays: 'Estimated Days',
        pricePerUnit: 'Price per Unit',
        trackingAvailable: 'Tracking Available',
        reliability: 'Reliability Rating',
        compare: 'Compare Companies'
      },
      // Orders
      orders: {
        title: 'Order Management',
        addOrder: 'New Order',
        editOrder: 'Edit Order',
        orderNumber: 'Order #',
        client: 'Dealer',
        endClient: 'End Client',
        supplier: 'Supplier',
        vehicle: 'Vehicle',
        shippingCompany: 'Shipping Company',
        status: 'Status',
        shippingCost: 'Shipping Cost',
        estimatedArrival: 'ETA',
        containerNumber: 'Container #',
        blNumber: 'BL #',
        trackingRef: 'Tracking Ref',
        currentLocation: 'Current Location',
        departureDate: 'Departure Date',
        statusHistory: 'Status History'
      },
      // Status labels
      status: {
        new_lead: 'New Lead',
        waiting_documents: 'Waiting Documents',
        documents_received: 'Documents Received',
        car_sourcing: 'Car Sourcing',
        vehicle_confirmed: 'Vehicle Confirmed',
        payment_pending: 'Payment Pending',
        payment_received: 'Payment Received',
        shipping_booked: 'Shipping Booked',
        in_transit: 'In Transit',
        container_loaded: 'Container Loaded',
        bl_issued: 'BL Issued',
        arrived_algeria: 'Arrived in Algeria',
        customs_clearance: 'Customs Clearance',
        ready_delivery: 'Ready for Delivery',
        delivered: 'Delivered',
        cancelled: 'Cancelled',
        // Car-level statuses
        pending: 'Pending',
        purchased: 'Purchased',
        arrived_port: 'Arrived at Port'
      },
      // Employees
      employees: {
        title: 'Employee Management',
        addEmployee: 'Add Employee',
        editEmployee: 'Edit Employee',
        role: 'Role',
        roles: {
          admin: 'Administrator',
          sales_agent: 'Sales Agent',
          logistics_manager: 'Logistics Manager',
          operations: 'Operations'
        }
      },
      // Common
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        view: 'View',
        search: 'Search...',
        filter: 'Filter',
        noData: 'No data found',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        confirm: 'Confirm',
        actions: 'Actions',
        upload: 'Upload',
        download: 'Download',
        total: 'Total',
        of: 'of'
      }
    }
  },
  fr: {
    translation: {
      nav: {
        dashboard: 'Tableau de bord',
        clients: 'Concessionnaires',
        suppliers: 'Fournisseurs',
        catalog: 'Catalogue Auto',
        brands: 'Marques',
        models: 'Modèles',
        vehicles: 'Véhicules',
        shipping: 'Transporteurs',
        orders: 'Commandes',
        employees: 'Employés',
        settings: 'Paramètres',
        logout: 'Déconnexion',
        portal: 'Mon Portail',
        profile: 'Profil',
        myOrders: 'Mes Commandes',
        documents: 'Documents'
      },
      auth: {
        login: 'Connexion',
        register: 'Inscription',
        email: 'Email',
        password: 'Mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        name: 'Nom complet',
        phone: 'Téléphone',
        forgotPassword: 'Mot de passe oublié?',
        noAccount: "Pas de compte?",
        hasAccount: 'Déjà un compte?',
        signUp: "S'inscrire",
        signIn: 'Se connecter',
        welcomeBack: 'Bon retour',
        createAccount: 'Créer un compte',
        adminLogin: 'Espace Personnel',
        clientLogin: 'Portail Client'
      },
      dashboard: {
        title: 'Tableau de bord',
        subtitle: 'Aperçu de vos opérations d\'import automobile',
        totalClients: 'Total Clients',
        totalCars: 'Total Voitures',
        pendingCars: 'Voitures en Attente',
        activeOrders: 'Commandes Actives',
        pendingDocs: 'Documents en attente',
        inTransit: 'En Transit',
        delivered: 'Livrées',
        expectedArrivals: 'Arrivées Prévues',
        recentOrders: 'Commandes Récentes',
        ordersByStatus: 'Commandes par Statut',
        carsByStatus: 'Voitures par Statut',
        viewAll: 'Tout voir',
        noOrders: 'Pas encore de commandes',
        noRecentOrders: 'Aucune commande récente',
        cars: 'voitures',
        dealer: 'Concessionnaire'
      },
      clients: {
        title: 'Gestion des Clients',
        addClient: 'Ajouter un Client',
        editClient: 'Modifier le Client',
        name: 'Nom',
        email: 'Email',
        phone: 'Téléphone',
        address: 'Adresse',
        nationalId: 'NIN',
        passport: 'Numéro de Passeport',
        dob: 'Date de Naissance',
        notes: 'Notes',
        orderHistory: 'Historique des Commandes',
        documents: 'Documents'
      },
      suppliers: {
        title: 'Gestion des Fournisseurs',
        addSupplier: 'Ajouter un Fournisseur',
        editSupplier: 'Modifier le Fournisseur',
        name: 'Nom de la Société',
        contactEmail: 'Email de Contact',
        contactPhone: 'Téléphone de Contact',
        address: 'Adresse',
        carBrands: 'Marques de Voitures',
        notes: 'Notes',
        active: 'Actif',
        inactive: 'Inactif'
      },
      vehicles: {
        title: 'Catalogue Auto',
        addVehicle: 'Ajouter un Véhicule',
        editVehicle: 'Modifier le Véhicule',
        brand: 'Marque',
        model: 'Modèle',
        year: 'Année',
        version: 'Version/Finition',
        fuelType: 'Carburant',
        transmission: 'Transmission',
        color: 'Couleur',
        price: 'Prix',
        photos: 'Photos',
        specs: 'Spécifications'
      },
      shipping: {
        title: 'Sociétés de Transport',
        addCompany: 'Ajouter une Société',
        editCompany: 'Modifier la Société',
        name: 'Nom de la Société',
        route: 'Itinéraire Maritime',
        estimatedDays: 'Jours Estimés',
        pricePerUnit: 'Prix par Unité',
        trackingAvailable: 'Suivi Disponible',
        reliability: 'Indice de Fiabilité',
        compare: 'Comparer les Sociétés'
      },
      orders: {
        title: 'Gestion des Commandes',
        addOrder: 'Nouvelle Commande',
        editOrder: 'Modifier la Commande',
        orderNumber: 'N° Commande',
        client: 'Concessionnaire',
        endClient: 'Client Final',
        supplier: 'Fournisseur',
        vehicle: 'Véhicule',
        shippingCompany: 'Transporteur',
        status: 'Statut',
        shippingCost: 'Coût de Transport',
        estimatedArrival: 'Date d\'Arrivée Estimée',
        containerNumber: 'N° Conteneur',
        blNumber: 'N° BL',
        trackingRef: 'Réf. de Suivi',
        currentLocation: 'Position Actuelle',
        departureDate: 'Date de Départ',
        statusHistory: 'Historique des Statuts'
      },
      employees: {
        title: 'Gestion des Employés',
        addEmployee: 'Ajouter un Employé',
        editEmployee: 'Modifier l\'Employé',
        role: 'Rôle',
        roles: {
          admin: 'Administrateur',
          sales_agent: 'Agent Commercial',
          logistics_manager: 'Responsable Logistique',
          operations: 'Opérations'
        }
      },
      status: {
        new_lead: 'Nouveau',
        waiting_documents: 'Attente Documents',
        documents_received: 'Documents Reçus',
        car_sourcing: 'Recherche Véhicule',
        vehicle_confirmed: 'Véhicule Confirmé',
        payment_pending: 'Paiement en Attente',
        payment_received: 'Paiement Reçu',
        shipping_booked: 'Transport Réservé',
        in_transit: 'En Transit',
        container_loaded: 'Conteneur Chargé',
        bl_issued: 'BL Émis',
        arrived_algeria: 'Arrivé en Algérie',
        customs_clearance: 'Dédouanement',
        ready_delivery: 'Prêt à Livrer',
        delivered: 'Livré',
        cancelled: 'Annulé',
        pending: 'En Attente',
        purchased: 'Acheté',
        arrived_port: 'Arrivé au Port'
      },
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        view: 'Voir',
        search: 'Rechercher...',
        filter: 'Filtrer',
        noData: 'Aucune donnée',
        loading: 'Chargement...',
        error: 'Erreur',
        success: 'Succès',
        confirm: 'Confirmer',
        actions: 'Actions',
        upload: 'Téléverser',
        download: 'Télécharger',
        total: 'Total',
        of: 'sur'
      }
    }
  },
  ar: {
    translation: {
      nav: {
        dashboard: 'لوحة التحكم',
        clients: 'العملاء',
        suppliers: 'الموردين',
        catalog: 'كتالوج السيارات',
        brands: 'العلامات التجارية',
        models: 'الموديلات',
        vehicles: 'المركبات',
        shipping: 'شركات الشحن',
        orders: 'الطلبات',
        employees: 'الموظفين',
        settings: 'الإعدادات',
        logout: 'تسجيل الخروج',
        portal: 'بوابتي',
        profile: 'الملف الشخصي',
        myOrders: 'طلباتي',
        documents: 'المستندات'
      },
      auth: {
        login: 'تسجيل الدخول',
        register: 'التسجيل',
        email: 'البريد الإلكتروني',
        password: 'كلمة المرور',
        confirmPassword: 'تأكيد كلمة المرور',
        name: 'الاسم الكامل',
        phone: 'رقم الهاتف',
        forgotPassword: 'نسيت كلمة المرور؟',
        noAccount: 'ليس لديك حساب؟',
        hasAccount: 'لديك حساب بالفعل؟',
        signUp: 'إنشاء حساب',
        signIn: 'دخول',
        welcomeBack: 'مرحباً بعودتك',
        createAccount: 'إنشاء حساب جديد',
        adminLogin: 'دخول الموظفين',
        clientLogin: 'بوابة العملاء'
      },
      dashboard: {
        title: 'لوحة التحكم',
        subtitle: 'نظرة عامة على عمليات استيراد السيارات',
        totalClients: 'إجمالي العملاء',
        totalCars: 'إجمالي السيارات',
        pendingCars: 'سيارات معلقة',
        activeOrders: 'الطلبات النشطة',
        pendingDocs: 'مستندات معلقة',
        inTransit: 'في الطريق',
        delivered: 'تم التسليم',
        expectedArrivals: 'الوصول المتوقع',
        recentOrders: 'الطلبات الأخيرة',
        ordersByStatus: 'الطلبات حسب الحالة',
        carsByStatus: 'السيارات حسب الحالة',
        viewAll: 'عرض الكل',
        noOrders: 'لا توجد طلبات بعد',
        noRecentOrders: 'لا توجد طلبات حديثة',
        cars: 'سيارات',
        dealer: 'وكيل'
      },
      clients: {
        title: 'إدارة العملاء',
        addClient: 'إضافة عميل',
        editClient: 'تعديل العميل',
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        phone: 'الهاتف',
        address: 'العنوان',
        nationalId: 'رقم التعريف الوطني',
        passport: 'رقم جواز السفر',
        dob: 'تاريخ الميلاد',
        notes: 'ملاحظات',
        orderHistory: 'سجل الطلبات',
        documents: 'المستندات'
      },
      suppliers: {
        title: 'إدارة الموردين',
        addSupplier: 'إضافة مورد',
        editSupplier: 'تعديل المورد',
        name: 'اسم الشركة',
        contactEmail: 'بريد التواصل',
        contactPhone: 'هاتف التواصل',
        address: 'العنوان',
        carBrands: 'ماركات السيارات',
        notes: 'ملاحظات',
        active: 'نشط',
        inactive: 'غير نشط'
      },
      vehicles: {
        title: 'كتالوج السيارات',
        addVehicle: 'إضافة مركبة',
        editVehicle: 'تعديل المركبة',
        brand: 'الماركة',
        model: 'الموديل',
        year: 'السنة',
        version: 'الإصدار/التجهيز',
        fuelType: 'نوع الوقود',
        transmission: 'ناقل الحركة',
        color: 'اللون',
        price: 'السعر',
        photos: 'الصور',
        specs: 'المواصفات'
      },
      shipping: {
        title: 'شركات الشحن',
        addCompany: 'إضافة شركة',
        editCompany: 'تعديل الشركة',
        name: 'اسم الشركة',
        route: 'خط الشحن',
        estimatedDays: 'الأيام المقدرة',
        pricePerUnit: 'السعر لكل وحدة',
        trackingAvailable: 'التتبع متاح',
        reliability: 'مؤشر الموثوقية',
        compare: 'مقارنة الشركات'
      },
      orders: {
        title: 'إدارة الطلبات',
        addOrder: 'طلب جديد',
        editOrder: 'تعديل الطلب',
        orderNumber: 'رقم الطلب',
        client: 'الوكيل',
        endClient: 'العميل النهائي',
        supplier: 'المورد',
        vehicle: 'المركبة',
        shippingCompany: 'شركة الشحن',
        status: 'الحالة',
        shippingCost: 'تكلفة الشحن',
        estimatedArrival: 'تاريخ الوصول المتوقع',
        containerNumber: 'رقم الحاوية',
        blNumber: 'رقم بوليصة الشحن',
        trackingRef: 'مرجع التتبع',
        currentLocation: 'الموقع الحالي',
        departureDate: 'تاريخ المغادرة',
        statusHistory: 'سجل الحالات'
      },
      employees: {
        title: 'إدارة الموظفين',
        addEmployee: 'إضافة موظف',
        editEmployee: 'تعديل الموظف',
        role: 'الدور',
        roles: {
          admin: 'مدير',
          sales_agent: 'مندوب مبيعات',
          logistics_manager: 'مدير اللوجستيك',
          operations: 'العمليات'
        }
      },
      status: {
        new_lead: 'جديد',
        waiting_documents: 'في انتظار المستندات',
        documents_received: 'تم استلام المستندات',
        car_sourcing: 'البحث عن سيارة',
        vehicle_confirmed: 'تأكيد السيارة',
        payment_pending: 'في انتظار الدفع',
        payment_received: 'تم استلام الدفع',
        shipping_booked: 'تم حجز الشحن',
        in_transit: 'في الطريق',
        container_loaded: 'تم تحميل الحاوية',
        bl_issued: 'تم إصدار بوليصة الشحن',
        arrived_algeria: 'وصل إلى الجزائر',
        customs_clearance: 'التخليص الجمركي',
        ready_delivery: 'جاهز للتسليم',
        delivered: 'تم التسليم',
        cancelled: 'ملغى',
        pending: 'قيد الانتظار',
        purchased: 'تم الشراء',
        arrived_port: 'وصل إلى الميناء'
      },
      common: {
        save: 'حفظ',
        cancel: 'إلغاء',
        delete: 'حذف',
        edit: 'تعديل',
        view: 'عرض',
        search: 'بحث...',
        filter: 'تصفية',
        noData: 'لا توجد بيانات',
        loading: 'جاري التحميل...',
        error: 'خطأ',
        success: 'نجاح',
        confirm: 'تأكيد',
        actions: 'الإجراءات',
        upload: 'رفع',
        download: 'تحميل',
        total: 'المجموع',
        of: 'من'
      }
    }
  },
  zh: {
    translation: {
      nav: {
        dashboard: '仪表板',
        clients: '客户',
        suppliers: '供应商',
        catalog: '汽车目录',
        brands: '品牌',
        models: '型号',
        vehicles: '车辆',
        shipping: '物流公司',
        orders: '订单',
        employees: '员工',
        settings: '设置',
        logout: '退出',
        portal: '我的门户',
        profile: '个人资料',
        myOrders: '我的订单',
        documents: '文件'
      },
      auth: {
        login: '登录',
        register: '注册',
        email: '邮箱',
        password: '密码',
        confirmPassword: '确认密码',
        name: '全名',
        phone: '电话',
        forgotPassword: '忘记密码？',
        noAccount: '没有账户？',
        hasAccount: '已有账户？',
        signUp: '注册',
        signIn: '登录',
        welcomeBack: '欢迎回来',
        createAccount: '创建账户',
        adminLogin: '员工登录',
        clientLogin: '客户门户'
      },
      dashboard: {
        title: '仪表板',
        subtitle: '汽车进口业务概览',
        totalClients: '客户总数',
        totalCars: '汽车总数',
        pendingCars: '待处理车辆',
        activeOrders: '活跃订单',
        pendingDocs: '待处理文件',
        inTransit: '运输中',
        delivered: '已交付',
        expectedArrivals: '预计到达',
        recentOrders: '最近订单',
        ordersByStatus: '按状态分类',
        carsByStatus: '车辆按状态分类',
        viewAll: '查看全部',
        noOrders: '暂无订单',
        noRecentOrders: '暂无最近订单',
        cars: '辆车',
        dealer: '经销商'
      },
      clients: {
        title: '客户管理',
        addClient: '添加客户',
        editClient: '编辑客户',
        name: '姓名',
        email: '邮箱',
        phone: '电话',
        address: '地址',
        nationalId: '身份证号',
        passport: '护照号码',
        dob: '出生日期',
        notes: '备注',
        orderHistory: '订单历史',
        documents: '文件'
      },
      suppliers: {
        title: '供应商管理',
        addSupplier: '添加供应商',
        editSupplier: '编辑供应商',
        name: '公司名称',
        contactEmail: '联系邮箱',
        contactPhone: '联系电话',
        address: '地址',
        carBrands: '汽车品牌',
        notes: '备注',
        active: '活跃',
        inactive: '停用'
      },
      vehicles: {
        title: '汽车目录',
        addVehicle: '添加车辆',
        editVehicle: '编辑车辆',
        brand: '品牌',
        model: '型号',
        year: '年份',
        version: '版本/配置',
        fuelType: '燃料类型',
        transmission: '变速箱',
        color: '颜色',
        price: '价格',
        photos: '照片',
        specs: '规格参数'
      },
      shipping: {
        title: '物流公司',
        addCompany: '添加公司',
        editCompany: '编辑公司',
        name: '公司名称',
        route: '航线',
        estimatedDays: '预计天数',
        pricePerUnit: '单价',
        trackingAvailable: '可追踪',
        reliability: '可靠性评分',
        compare: '对比公司'
      },
      orders: {
        title: '订单管理',
        addOrder: '新建订单',
        editOrder: '编辑订单',
        orderNumber: '订单号',
        client: '经销商',
        endClient: '最终客户',
        supplier: '供应商',
        vehicle: '车辆',
        shippingCompany: '物流公司',
        status: '状态',
        shippingCost: '运费',
        estimatedArrival: '预计到达',
        containerNumber: '集装箱号',
        blNumber: '提单号',
        trackingRef: '追踪编号',
        currentLocation: '当前位置',
        departureDate: '出发日期',
        statusHistory: '状态历史'
      },
      employees: {
        title: '员工管理',
        addEmployee: '添加员工',
        editEmployee: '编辑员工',
        role: '角色',
        roles: {
          admin: '管理员',
          sales_agent: '销售代理',
          logistics_manager: '物流经理',
          operations: '运营'
        }
      },
      status: {
        new_lead: '新线索',
        waiting_documents: '等待文件',
        documents_received: '文件已收',
        car_sourcing: '寻找车源',
        vehicle_confirmed: '车辆确认',
        payment_pending: '待付款',
        payment_received: '已付款',
        shipping_booked: '已订舱',
        in_transit: '运输中',
        container_loaded: '已装箱',
        bl_issued: '提单已签发',
        arrived_algeria: '已到阿尔及利亚',
        customs_clearance: '清关中',
        ready_delivery: '待交付',
        delivered: '已交付',
        cancelled: '已取消',
        pending: '待处理',
        purchased: '已购买',
        arrived_port: '已到港'
      },
      common: {
        save: '保存',
        cancel: '取消',
        delete: '删除',
        edit: '编辑',
        view: '查看',
        search: '搜索...',
        filter: '筛选',
        noData: '暂无数据',
        loading: '加载中...',
        error: '错误',
        success: '成功',
        confirm: '确认',
        actions: '操作',
        upload: '上传',
        download: '下载',
        total: '总计',
        of: '/'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

export default i18n;
